import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ConsentDecision = 'granted' | 'denied' | null;

/**
 * Einwilligung fuer Marketing-Speicherung.
 *
 * Hintergrund: TDDDG §25 verlangt Opt-in fuer jede nicht notwendige Speicherung
 * auf dem Endgeraet. Das betrifft die dauerhafte First-Touch-Attribution im
 * localStorage und spaeter die Pixel von Meta und Google.
 *
 * Nicht einwilligungspflichtig und deshalb bewusst ausgenommen:
 * - Das Auslesen von UTM- und Klick-Parametern aus der URL. Das ist kein
 *   Speicherzugriff.
 * - Die sessionbezogene Ablage dieser Parameter, um den gerade laufenden
 *   Vorgang der Bestellung zuordnen zu koennen.
 * - Diese Entscheidung selbst. Sie zu merken ist notwendig, um den Nutzer
 *   nicht bei jedem Aufruf erneut zu fragen.
 */
@Injectable({
  providedIn: 'root',
})
export class ConsentService {
  private readonly storageKey = 'melowish_consent_marketing';
  private readonly decisionSubject: BehaviorSubject<ConsentDecision>;

  constructor() {
    this.decisionSubject = new BehaviorSubject<ConsentDecision>(this.readStored());
  }

  /** Aktuelle Entscheidung. null = noch nicht gefragt. */
  get decision(): ConsentDecision {
    return this.decisionSubject.value;
  }

  get decision$(): Observable<ConsentDecision> {
    return this.decisionSubject.asObservable();
  }

  /** true, wenn dauerhafte Marketing-Speicherung erlaubt ist. */
  hasMarketingConsent(): boolean {
    return this.decisionSubject.value === 'granted';
  }

  /** true, solange noch keine Entscheidung vorliegt - dann muss gefragt werden. */
  needsDecision(): boolean {
    return this.decisionSubject.value === null;
  }

  grantMarketing(): void {
    this.store('granted');
  }

  denyMarketing(): void {
    this.store('denied');
  }

  /**
   * Setzt die Entscheidung zurueck und raeumt auf, was nur mit Einwilligung
   * gespeichert werden durfte. Wird fuer den Widerruf gebraucht.
   */
  revoke(): void {
    this.store(null);
    try {
      localStorage.removeItem('melowish_first_attribution');
    } catch {
      // Storage kann blockiert sein - dann gibt es auch nichts aufzuraeumen.
    }
  }

  private store(decision: ConsentDecision): void {
    try {
      if (decision === null) {
        localStorage.removeItem(this.storageKey);
      } else {
        localStorage.setItem(this.storageKey, decision);
      }
    } catch {
      // Privater Modus oder blockierter Storage: Entscheidung gilt dann nur
      // fuer diese Sitzung, im Zweifel also "nicht gespeichert".
    }
    this.decisionSubject.next(decision);
  }

  private readStored(): ConsentDecision {
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw === 'granted' || raw === 'denied' ? raw : null;
    } catch {
      return null;
    }
  }
}
